/**
 * LogTable.jsx — the grouped log table.
 *
 * Parent row = one search query (GET /?groupBy=query); children = the attempts
 * logged against it. Ocean makes a single Freightify call per search, so a
 * group holds one child per attempt / re-search — not one per carrier the way
 * the air log fans out. Instead, the SHIPPING LINE column lists every unique
 * line that quoted on that attempt (`CODE - Name`, from each offer's
 * `meta.serviceProvider`); the parent row carries only the count from the
 * latest rate search, so the collapsed view stays one line tall.
 */
import React from "react";
import { ChevronDown, ChevronUp, Eye, Ship } from "lucide-react";
import { toast } from "react-toastify";
import { copyToClipboard, formatCreatedAt, formatNumber, formatSno, getVariantMeta, portCode } from "./logUtils";

const HEAD_CLASS = "px-4 py-3 text-[11px] font-semibold tracking-[0.06em] text-gray-500 uppercase text-left";

/**
 * The row's tag. A failed call and a re-search carry their own variant, so
 * "Freightify Rate Search Error" / "… Update" is the chip itself — there's no
 * separate failed marker or status-code badge on the row.
 */
function TypeChip({ variant, label }) {
  const meta = getVariantMeta(variant);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium ${meta.chip}`}
      title={label || meta.label}
    >
      <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: meta.dot }} />
      {label || meta.label}
    </span>
  );
}

/** Codes only (`INNSA1 → DEBFN`); the full port names stay in the tooltip. */
function Route({ origin, destination }) {
  if (!origin && !destination) return <span className="text-gray-400">—</span>;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-700 tracking-wide whitespace-nowrap"
      title={`${origin || "—"} → ${destination || "—"}`}
    >
      {portCode(origin) || "—"}
      <span className="text-primary">&#8594;</span>
      {portCode(destination) || "—"}
    </span>
  );
}

/**
 * Every line on a row as a `CODE - Full name` pill, no logo. A row logged
 * before line codes were derived has only the name, so the code half is
 * dropped rather than faked — as it is when a booking payload named the line
 * by its SCAC alone and `deriveBookingLines` had to use that as the name too,
 * which would otherwise print as `MAEU - MAEU`.
 */
function ShippingLines({ lines, fallback = "No line quoted" }) {
  if (!lines?.length) {
    // A node (the error chip) is passed through as-is; plain text gets the
    // muted "nothing quoted" treatment.
    return typeof fallback === "string" ? (
      <span className="text-[13px] text-gray-400">{fallback}</span>
    ) : (
      fallback
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {lines.map((line) => {
        const hasCode = Boolean(line.code) && line.code.toLowerCase() !== (line.name || "").toLowerCase();
        return (
          <span
            key={line.code || line.name}
            title={hasCode ? `${line.name} (${line.code})` : line.name}
            className="px-2.5 py-1 rounded-full bg-[#F7F8FA] border border-gray-200 text-[12px] font-medium text-gray-700 whitespace-nowrap"
          >
            {hasCode ? `${line.code} - ${line.name}` : line.name}
          </span>
        );
      })}
    </span>
  );
}

/**
 * The serial as the column shows it. The ObjectId itself is never printed —
 * it's the tooltip and what a click puts on the clipboard, which is all it was
 * ever needed for.
 */
function SerialId({ sno, id, label, className = "" }) {
  const handleCopy = async (event) => {
    // The group row toggles on click; copying must not expand it too.
    event.stopPropagation();
    if (!id) return;
    const ok = await copyToClipboard(id);
    toast[ok ? "success" : "error"](ok ? `${label} copied` : "Could not copy to clipboard");
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!id}
      title={id ? `${id} — click to copy` : "No id logged"}
      className={`font-mono text-[13px] text-left ${id ? "hover:text-primary" : "cursor-default"} ${className}`}
    >
      {formatSno(sno)}
    </button>
  );
}

function LogTable({ groups, expandedKeys, onToggleGroup, onViewLog }) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-x-auto bg-white">
      <table className="w-full min-w-[1160px] border-collapse">
        <thead>
          <tr className="bg-[#F1F3F5]">
            {/* Holds the expand affordance only — the serials themselves sit
                under Query ID, next to the id each one numbers. */}
            <th className={`${HEAD_CLASS} w-[70px]`}>S.no</th>
            <th className={`${HEAD_CLASS} w-[170px]`}>Query ID</th>
            <th className={`${HEAD_CLASS} w-[150px]`}>Route</th>
            <th className={`${HEAD_CLASS} w-[320px]`}>Shipping line</th>
            <th className={`${HEAD_CLASS} w-[200px]`}>Log type</th>
            <th className={`${HEAD_CLASS} w-[110px]`}>Containers</th>
            <th className={`${HEAD_CLASS} w-[110px]`}>Weight (kg)</th>
            <th className={`${HEAD_CLASS} w-[150px]`}>User</th>
            <th className={`${HEAD_CLASS} w-[230px]`}>Created</th>
            <th className={`${HEAD_CLASS} w-[70px] text-center`}>View</th>
          </tr>
        </thead>

        <tbody>
          {groups.map((group) => {
            const expanded = expandedKeys.includes(group.key);

            return (
              <React.Fragment key={group.key}>
                <tr
                  onClick={() => onToggleGroup(group.key)}
                  className={`cursor-pointer border-b transition-colors ${
                    expanded ? "bg-[#FFFBF0] border-[#F6E3B4]" : "bg-white border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <td className="px-4 py-3.5 text-gray-500">
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </td>
                  <td className="px-4 py-3.5">
                    <SerialId
                      sno={group.query_sno}
                      id={group.query_id}
                      label="Query ID"
                      className="font-semibold text-gray-800"
                    />
                  </td>
                  <td className="px-4 py-3.5">
                    <Route origin={group.origin} destination={group.destination} />
                  </td>
                  {/* Count only — how many lines the latest rate search quoted.
                      The lines themselves are chips on the attempt rows below. */}
                  <td className="px-4 py-3.5">
                    {group.carriers_count === 0 && group.failed_count > 0 ? (
                      // Nothing quoted because the calls failed — "0 lines"
                      // would read as an empty result, so say how many errored
                      // (one chip per failure sits on the rows below).
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-[12px] font-medium text-red-700">
                        <span className="w-[6px] h-[6px] rounded-full bg-red-500" />
                        {group.failed_count} {group.failed_count === 1 ? "error" : "errors"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
                        <Ship size={14} className="text-primary" />
                        {group.carriers_count} {group.carriers_count === 1 ? "line" : "lines"}
                      </span>
                    )}
                  </td>
                  {/* One chip per distinct tag on the query — an error tag here
                      is how a failed attempt shows on the collapsed row. */}
                  <td className="px-4 py-3.5">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {group.types.map((entry) => (
                        <TypeChip key={entry.key} variant={entry.key} label={entry.label} />
                      ))}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-[13px] text-gray-700">
                    {formatNumber(group.total_containers)}
                    {group.container_load && (
                      <span className="ml-1 text-[11px] text-gray-400">{group.container_load}</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-[13px] text-gray-700">{formatNumber(group.total_weight)}</td>
                  <td className="px-4 py-3.5 text-[13px] text-gray-700">{group.requested_by}</td>
                  <td className="px-4 py-3.5 text-[12px] font-mono text-gray-400">
                    {formatCreatedAt(group.created_at)}
                  </td>
                  {/* No view action on the group row — a query has no single
                      payload to show; the eye lives on each attempt below. */}
                  <td className="px-4 py-3.5" />
                </tr>

                {expanded &&
                  group.logs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-100 bg-white hover:bg-[#FAFAFA]">
                      <td className="px-4 py-3.5" />
                      <td className="px-4 py-3.5 pl-8">
                        <SerialId sno={log.sno} id={log.id} label="Log ID" className="font-medium text-gray-600" />
                      </td>
                      <td className="px-4 py-3.5" />
                      {/* Booking / update / cancel rows name the line they were
                          made with, read out of the payload (deriveBookingLines). */}
                      <td className="px-4 py-3.5">
                        <ShippingLines
                          lines={log.shipping_lines}
                          fallback={
                            log.failed ? (
                              <TypeChip variant={log.variant} />
                            ) : log.type === "rate_search" ? (
                              "No line quoted"
                            ) : (
                              "—"
                            )
                          }
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <TypeChip variant={log.variant} />
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-700">
                        {formatNumber(log.total_containers)}
                        {log.container_load && (
                          <span className="ml-1 text-[11px] text-gray-400">{log.container_load}</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-700">{formatNumber(log.total_weight)}</td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-700" title={log.requested_by_email || ""}>
                        {log.requested_by}
                      </td>
                      {/* No status-code badge — the error chip in Log type says
                          it, and the code itself is in the detail modal. */}
                      <td className="px-4 py-3.5 text-[12px] font-mono text-gray-400">
                        {formatCreatedAt(log.created_at)}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <button
                          type="button"
                          title="View log details"
                          onClick={() => onViewLog(log)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-[#FFF4D6]"
                        >
                          <Eye size={17} />
                        </button>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}

          {groups.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-14 text-center text-[13px] text-gray-400">
                No ocean API logs match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default LogTable;
